"use client"

import { useEffect, useRef } from "react"
import { useWorkflow } from "@/context/workflow-context-simple"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useTranslation } from '@/hooks/use-translation'

interface ColumnContextMenuProps {
  position: { top: number; left: number }
  columnId: string
  onClose: () => void
}

export function ColumnContextMenu({ position, columnId, onClose }: ColumnContextMenuProps) {
  const { openBulkEditModal, selectedItems, state, setPinnedColumn } = useWorkflow()
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose])

  const handleBulkEdit = () => {
    openBulkEditModal(columnId)
    onClose()
  }

  // Determine pinned for current stage
  const currentStageConfig = state.stages.find(s => s.stageCode === state.currentStage)
  const pinnedMap = state.pinnedColumnsByStage || {}
  const isPinned = currentStageConfig ? pinnedMap[currentStageConfig.stageId] === columnId : false
  const handleTogglePin = () => {
    setPinnedColumn(isPinned ? null : columnId)
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border rounded shadow-lg py-1 z-[9999]"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="w-full text-left px-4 py-2 hover:bg-gray-100"
              onClick={handleTogglePin}
            >
              {isPinned ? 'Unpin column' : 'Pin column'}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isPinned ? t('unpinTooltip' as any) : 'Pin this column to the left'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <button
        className="w-full text-left px-4 py-2 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
        onClick={handleBulkEdit}
        disabled={selectedItems.length === 0}
      >
        Bulk edit {selectedItems.length} items
      </button>
    </div>
  )
}
